import React, {
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import {
  ECampaignStatus,
  EParamSortOrder,
  EStatus,
  EDealType,
} from '@unfoldrtech/portal-mic'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  EChannelTypeGlobal,
  EDatasetKey,
  EReportColumnHeader,
  IDAReportCampaignsTable,
  IDOOHReportCampaignsTable,
  IGenericReportCampaignsTable,
  IGenericReportCampaignsTableRow,
  ISPReportCampaignsTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { TReportingFilters, TTranslationKey } from '../../../../models/types'
import { selectCampaignsTableReportingFilters } from '../../../../store/reportingFilters'
import {
  selectRefreshTable,
  setRefreshTable,
} from '../../../../store/refreshTable'
import { ReportTableV3 } from '../../components/ReportTableV3'
import { TableDataCellImpressions } from '../../components/table-data-cell/TableDataCellImpressions'
import { TableDataCellCosts } from '../../components/table-data-cell/TableDataCellCosts'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { AppContext } from '../../../../models'
import { TReportTableDataV3 } from '../../models/types/report-table-data-v3.type'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { useGetDefaultSortBy } from '../../hooks/use-get-default-sort-by'
import {
  TDACampaignsTableHeaders,
  TDOOHCampaignsTableHeaders,
  TGenericCampaignsTableHeaders,
  TSPCampaignsTableHeaders,
} from '../models'
import { TableDataCellCtr } from '../../components/table-data-cell/TableDataCellCtr'
import {
  TDatasetWhitelistKey,
  TGlobalTableDataType,
  TGlobalTables,
} from '../../models'
import { TableDataCellCpc } from '../../components/table-data-cell/TableDataCellCpc'
import { TableDataCellClicks } from '../../components/table-data-cell/TableDataCellClicks'
import { TableDataCellAvgRank } from '../../components/table-data-cell/TableDataCellAvgRank'
import { TableDataCellCarts } from '../../components/table-data-cell/TableDataCellCarts'
import { TableDataCellPurchases } from '../../components/table-data-cell/TableDataCellPurchases'
import { TableDataCellRevenue } from '../../components/table-data-cell/TableDataCellRevenue'
import { TableDataCellRoas } from '../../components/table-data-cell/TableDataCellRoas'
import { TableDataCellFrequency } from '../../components/table-data-cell/TableDataCellFrequency'
import { TableDataCellUniqueReach } from '../../components/table-data-cell/TableDataCellUniqueReach'
import { TableDataCellCpm } from '../../components/table-data-cell/TableDataCellCpm'
import { getCampaignsEDatasetKeyFromChannelTypeGlobal } from '../helpers/get-campaigns-e-dataset-key-from-channel-type-global'
import { getTableAndChartCampaignsFn } from '../helpers/get-table-and-chart-campaigns-fn'
import { TableDataCellAdvertiserName } from '../../components/table-data-cell/TableDataCellAdvertiserName'
import { TableDataCellStatusCampaignWithUpdate } from '../../components/table-data-cell/TableDataCellStatusCampaignWithUpdate'
import { TableDataCellChannelType } from '../../components/table-data-cell/TableDataCellChannelType'
import { TableDataCellBudget } from '../../components/table-data-cell/TableDataCellBudget'
import { TableDataCellStartDate } from '../../components/table-data-cell/TableDataCellStartDate'
import { TableDataCellEndDate } from '../../components/table-data-cell/TableDataCellEndDate'
import { TableDataCellShareOfVoice } from '../../components/table-data-cell/TableDataCellShareOfVoice'
import { TableDataCellWinRate } from '../../components/table-data-cell/TableDataCellWinRate'
import { TableDataCellNewToBrandPurchases } from '../../components/table-data-cell/TableDataCellNewToBrandPurchases'
import { TableDataCellNewToBrandRevenue } from '../../components/table-data-cell/TableDataCellNewToBrandRevenue'
import { TableDataCellPlayouts } from '../../components/table-data-cell/TableDataCellPlayouts'
import { TableDataCellCampaignName } from '../../components/table-data-cell/TableDataCellCampaignName'
import { TableDataCellCampaignType } from '../../components/table-data-cell/TableDataCellCampaignType'
import { TableDataCellConversionRate } from '../../components/table-data-cell/TableDataCellConversionRate'
import useShowToast from '../../../../hooks/useShowToast'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { TablePreloader } from '../../components/table-preloader'
import { setReportingData } from '../../../../store/reportingData'

type TCampaignsTableHeaders = TGenericCampaignsTableHeaders &
  TSPCampaignsTableHeaders &
  TDACampaignsTableHeaders &
  TDOOHCampaignsTableHeaders
type IReportCampaignsTable =
  | IGenericReportCampaignsTable
  | ISPReportCampaignsTable
  | IDAReportCampaignsTable
  | IDOOHReportCampaignsTable

const PAGE_LIMIT = 50
export const ReportingCampaignsTableWrapper = ({
  onTableDataChange,
  channelType,
  isHome,
}: {
  onTableDataChange: (campaignsTableData: IReportCampaignsTable) => void
  channelType?: EChannelTypeGlobal
  isHome?: boolean
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [appContext] = useContext(AppContext)

  const {
    startDate,
    endDate,
    timeWindow,
    tzZone,
    filterBy,
    filterOperation,
    filterValue,
    name,
    status,
  } = useSelector(selectCampaignsTableReportingFilters)
  const refreshTable = useSelector(selectRefreshTable)
  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()

  const orgType = appContext.userOrg!.type
  const hookFn = getTableAndChartCampaignsFn({
    orgType,
  })

  const getTableDataFn = hookFn.tableFn!

  const [sortBy, setSortBy] = useState<TCampaignsTableHeaders>()
  const [sortOrder, setSortOrder] = useState<EParamSortOrder>()
  const [tableData, setTableData] = useState<TReportTableDataV3>()

  const [errorUpdateStatus, setErrorUpdateStatus] = useState<TTranslationKey>()
  const [showUpdateStatusErrorToast, setShowUpdateStatusErrorToast] =
    useShowToast(false)
  const [hasNextPage, setHasNextPage] = useState(true)

  useGetDefaultSortBy({
    sortBy,
    sortOrder,
    // @ts-expect-error: fix in PORT-2535
    setSortBy,
    setSortOrder,
    orgType,
    channelType,
  })

  const {
    data: tableDataResponseObj,
    refetch,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = getTableDataFn({
    retailerId,
    advertiserId,
    channelType,
    sortBy,
    timeWindow,
    startDate,
    endDate,
    sortOrder,
    tzZone,
    filterBy,
    filterOperation,
    filterValue,
    name: name ? encodeURI(name) : undefined,
    status,
    enabled: false,
    pageLimit: PAGE_LIMIT,
    pageOffset: 0,
  })

  const handleLoadNextPage = useCallback(() => {
    fetchNextPage()
  }, [])

  const onSortByChange = (sortColumn?: TGlobalTables) => {
    setSortBy(sortColumn as TCampaignsTableHeaders)
  }

  const onSortOrderChange = (
    type: TReportingFilters['sortOrder'] = EParamSortOrder.Desc
  ) => {
    setSortOrder(type)
  }

  const tableDataResponse = useMemo(() => {
    if (!tableDataResponseObj) {
      return {
        datasetKey: '' as EDatasetKey,
        rows: [],
      }
    }

    const lastPageRows =
      tableDataResponseObj.pages[tableDataResponseObj.pages.length - 1]?.data
        .table?.rows

    if (lastPageRows !== undefined && lastPageRows.length < PAGE_LIMIT) {
      setHasNextPage(false)
    }

    const payload: IReportCampaignsTable = {
      datasetKey: tableDataResponseObj.pages[0].data.table
        ?.datasetKey as unknown as EDatasetKey,
      rows: (tableDataResponseObj?.pages?.flatMap(
        (page) => page?.data?.table?.rows.flatMap((row) => row) ?? []
      ) ?? []) as IGenericReportCampaignsTableRow[],
    }

    return payload
  }, [tableDataResponseObj])

  const whitelistDataList = useGetDatasetKeyTableWhitelist({
    key:
      (tableDataResponse.datasetKey as TDatasetWhitelistKey) ||
      getCampaignsEDatasetKeyFromChannelTypeGlobal(channelType),
  })

  const processTableDataResponse = (tableDataRes: IReportCampaignsTable) => {
    const responseHeaders = Array.from(
      new Set(tableDataRes.rows.map((row) => Object.keys(row)).flat())
    )

    const whitelistData = whitelistDataList
      .map((wData) => {
        const whitelistItem = responseHeaders.find(
          (header) => wData.header === header
        )
        if (whitelistItem) {
          return wData
        }
        return undefined
      })
      .filter((wData) => wData) as Array<TGlobalTableDataType>

    const headers = whitelistData.map((wData) => wData.header)

    const processedTableData: TReportTableDataV3 = {
      columnIdList: headers,
      columnNameList: whitelistData.map((wData) => wData.translatedMetricName),
      columnNameTooltipList: whitelistData.map(
        (wData) => wData.translatedTooltipMetricName
      ),
      columnSpanList: headers.map((header) =>
        header === EReportColumnHeader.Name ? 6 : 2
      ),
      rowList: [],
    }

    tableDataRes.rows.forEach((row) => {
      const dataRow: Array<ReactNode | ReactNode[]> = []
      headers.forEach((header) => {
        let dataCell: ReactNode | ReactNode[]
        const dataValue = row[header as TCampaignsTableHeaders]
        const navLink = isHome
          ? `../analytics/campaigns/${row.id}`
          : `../campaigns/${row.id}`

        switch (header) {
          case EReportColumnHeader.Name:
            dataCell = (
              <TableDataCellCampaignName
                dataValue={dataValue?.toString()}
                navigate={() => {
                  navigate(navLink, { state: row })
                }}
              />
            )
            break
          case EReportColumnHeader.CampaignType:
            dataCell = (
              <TableDataCellCampaignType dataValue={dataValue as EDealType} />
            )
            break
          case EReportColumnHeader.AdvertiserName:
            dataCell = (
              <TableDataCellAdvertiserName dataValue={dataValue?.toString()} />
            )
            break
          case EReportColumnHeader.Status:
            dataCell = (
              <TableDataCellStatusCampaignWithUpdate
                dataValue={dataValue?.toString() as EStatus}
                orgType={orgType}
                id={row.id}
                key={`${row.id}-${sortBy}-${sortOrder}`}
                isDirectDeal={row.campaign_type !== EDealType.Programmatic}
                isCompleted={
                  (row.status as unknown as ECampaignStatus) ===
                  ECampaignStatus.Completed
                }
                setErrorUpdateStatus={setErrorUpdateStatus}
                setShowUpdateStatusErrorToast={setShowUpdateStatusErrorToast}
              />
            )
            break
          case EReportColumnHeader.ChannelType:
            dataCell = (
              <TableDataCellChannelType
                dataValue={dataValue as EChannelTypeGlobal}
              />
            )
            break
          case EReportColumnHeader.Budget:
            dataCell = <TableDataCellBudget dataValue={dataValue as number} />
            break
          case EReportColumnHeader.StartDate:
            dataCell = (
              <TableDataCellStartDate dataValue={dataValue as string} />
            )
            break
          case EReportColumnHeader.EndDate:
            dataCell = <TableDataCellEndDate dataValue={dataValue as string} />
            break
          case EReportColumnHeader.Impressions:
            dataCell = (
              <TableDataCellImpressions dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.Clicks:
            dataCell = <TableDataCellClicks dataValue={Number(dataValue)} />
            break

          case EReportColumnHeader.Ctr:
            dataCell = <TableDataCellCtr dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Costs:
            dataCell = <TableDataCellCosts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Carts:
            dataCell = <TableDataCellCarts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Purchases:
            dataCell = <TableDataCellPurchases dataValue={Number(dataValue)} />
            break

          case EReportColumnHeader.Revenue:
            dataCell = <TableDataCellRevenue dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Roas:
            dataCell = <TableDataCellRoas dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.ShareOfVoice:
            dataCell = (
              <TableDataCellShareOfVoice dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.WinRate:
            dataCell = <TableDataCellWinRate dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.Cpm:
            dataCell = <TableDataCellCpm dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.NewToBrandPurchases:
            dataCell = (
              <TableDataCellNewToBrandPurchases dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.NewToBrandRevenue:
            dataCell = (
              <TableDataCellNewToBrandRevenue dataValue={Number(dataValue)} />
            )
            break
          // SP Exclusive
          case EReportColumnHeader.Cpc:
            dataCell = <TableDataCellCpc dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.AvgRank:
            dataCell = <TableDataCellAvgRank dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.ConversionRate:
            dataCell = (
              <TableDataCellConversionRate dataValue={Number(dataValue)} />
            )
            break
          // DA Exclusive
          case EReportColumnHeader.Frequency:
            dataCell = <TableDataCellFrequency dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.UniqueReach:
            dataCell = (
              <TableDataCellUniqueReach dataValue={Number(dataValue)} />
            )
            break
          case EReportColumnHeader.PvCarts:
            dataCell = <TableDataCellCarts dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.PvPurchases:
            dataCell = <TableDataCellPurchases dataValue={Number(dataValue)} />
            break
          case EReportColumnHeader.PvRevenue:
            dataCell = <TableDataCellRevenue dataValue={Number(dataValue)} />
            break
          // DOOH Exclusive
          case EReportColumnHeader.Playouts:
            dataCell = <TableDataCellPlayouts dataValue={Number(dataValue)} />
            break

          case EReportColumnHeader.PvRoas:
            dataCell = <TableDataCellPlayouts dataValue={Number(dataValue)} />
            break

          default:
            dataCell = undefined
            break
        }

        if (dataCell) {
          dataRow.push(dataCell)
        }
      })

      processedTableData.rowList.push(dataRow)
    })

    return processedTableData
  }

  useEffect(() => {
    if (tableDataResponse.datasetKey) {
      dispatch(setReportingData({ datasetKey: tableDataResponse.datasetKey }))
    }
  }, [tableDataResponse.datasetKey])

  useEffect(() => {
    if (sortBy && sortOrder && startDate && endDate) {
      refetch()
    }
  }, [sortBy, sortOrder, startDate, endDate, status])

  useEffect(() => {
    if (refreshTable) {
      refetch()
      dispatch(setRefreshTable({ refreshTable: false }))
    }
  }, [refreshTable])

  useEffect(() => {
    if (Object.keys(tableDataResponse).length > 0) {
      setTableData(processTableDataResponse(tableDataResponse))
      onTableDataChange(
        tableDataResponse as ISPReportCampaignsTable & IDAReportCampaignsTable
      )
    }
  }, [tableDataResponse])

  return (
    <>
      {tableData && (
        <ReportTableV3
          {...tableData}
          sortBy={sortBy}
          sortOrder={sortOrder}
          setSortBy={onSortByChange}
          setSortOrder={onSortOrderChange}
          pagination={{
            // NB: reporting service does not send us total items available based on filters (not pagination)
            // therefore one way we decide to not call the endpoint is if the returned items is less than the page limit
            total: hasNextPage ? Infinity : tableDataResponse.rows.length,
            loadNext: handleLoadNextPage,
          }}
          loading={isLoading || isFetchingNextPage}
        />
      )}

      <ErrorToast
        show={showUpdateStatusErrorToast}
        onClose={() => {
          setShowUpdateStatusErrorToast(false)
          setErrorUpdateStatus(undefined)
        }}
        translationKey={errorUpdateStatus}
      />

      {isFetching && <TablePreloader />}
    </>
  )
}
